export type RouteMetadata = {
  title: string;
  description: string;
};

const DEFAULT_DESCRIPTION =
  'Monitor, control, and verify the long-term performance of your Powston-managed energy asset.';

function normalizeMetadataText(value: string | null): string | null {
  return value
    ?.trim()
    .replace(/\s+/g, ' ')
    .toLowerCase() ?? null;
}

type RouteDefinition = {
  pattern: RegExp;
  title: string | ((match: RegExpMatchArray) => string);
  description: string;
};

const routes: RouteDefinition[] = [
  { pattern: /^\/$/, title: 'Home', description: 'View your Powston energy assets and their current status.' },
  { pattern: /^\/sites$/, title: 'Sites', description: 'Search and review the sites and inverters you can access.' },
  { pattern: /^\/summary\/([^/]+)$/, title: 'Site summary', description: 'Review current operation, readiness, and verified performance for this site.' },
  { pattern: /^\/site\/([^/]+)\/power(?:\/.*)?$/, title: 'Site power', description: 'Review power usage reported by this site’s meters.' },
  { pattern: /^\/site\/power\/([^/]+)(?:\/.*)?$/, title: 'Site power', description: 'Review power usage reported by this site’s meters.' },
  { pattern: /^\/site\/energy\/([^/]+)(?:\/.*)?$/, title: 'Site energy graph', description: 'Review site energy usage, costs, and earnings over time.' },
  { pattern: /^\/site\/modbus\/([^/]+)(?:\/.*)?$/, title: 'Site communications', description: 'Inspect controller, network, and Modbus communications for this site.' },
  // Ahead of the generic /site/{id} rule below, which would otherwise title
  // the Tuning page "Site details".
  { pattern: /^\/site\/([^/]+)\/tuning(?:\/.*)?$/, title: 'Site tuning', description: 'Guide how Powston trades this site — reserves, export margins, and EV charging windows.' },
  { pattern: /^\/site\/([^/]+)(?:\/.*)?$/, title: 'Site details', description: 'View and manage site configuration, equipment, and technical details.' },
  { pattern: /^\/inverter\/([^/]+)$/, title: 'Inverter', description: 'View live inverter operation, controls, and connection status.' },
  { pattern: /^\/inverter_actions\/([^/]+)$/, title: 'Inverter activity', description: 'Review the inverter decision and performance history.' },
  { pattern: /^\/inverter_action_details\/([^/]+)$/, title: 'Decision details', description: 'Inspect the evidence and inputs for a five-minute control decision.' },
  { pattern: /^\/inverter_settings\/([^/]+)$/, title: 'Inverter settings', description: 'Review and update inverter configuration.' },
  { pattern: /^\/assistant(?:\/[^/]+)?$/, title: 'Ask Powston', description: 'Ask Powston about site operation, performance, and next steps.' },
  { pattern: /^\/controls\/([^/]+)$/, title: 'Site controls', description: 'Manage operating modes and manual controls for this site.' },
  { pattern: /^\/rates(?:\/.*)?$/, title: 'Rates and performance', description: 'Compare energy rates and verified site performance.' },
  { pattern: /^\/settings(?:\/.*)?$/, title: 'Settings', description: 'Manage your Powston account and site preferences.' },
  { pattern: /^\/profile$/, title: 'Your profile', description: 'Manage your Powston profile and connected services.' },
  { pattern: /^\/configure$/, title: 'Install and recovery', description: 'Connect, commission, or recover site hardware.' },
  { pattern: /^\/controller_link\/[^/]+$/, title: 'Connect controller', description: 'Connect a Powston controller to the correct customer account and site.' },
  { pattern: /^\/controller_health\/[^/]+$/, title: 'Controller health', description: 'Review controller connectivity, telemetry, and operational health.' },
  { pattern: /^\/monitor\/[^/]+$/, title: 'Controller monitor', description: 'Monitor controller connectivity and recent operational activity.' },
  { pattern: /^\/installer\/knowledge$/, title: 'Installer knowledge', description: 'Search Powston installer guidance and technical knowledge.' },
  { pattern: /^\/installer(?:\/.*)?$/, title: 'Installer administration', description: 'Manage installer sites, users, and commissioning work.' },
  { pattern: /^\/admin(?:\/.*)?$/, title: 'Administration', description: 'Manage Powston platform operations.' },
  { pattern: /^\/login$/, title: 'Sign in', description: 'Sign in to your Powston account.' },
  { pattern: /^\/signup$/, title: 'Create account', description: 'Check suitability before sending a bill for a Powston-designed and installed energy system.' },
  { pattern: /^\/passkey(?:_create)?$/, title: 'Passkeys', description: 'Use or configure a passkey for secure account access.' },
  { pattern: /^\/privacy$/, title: 'Privacy Policy', description: 'How Powston collects, uses, and protects data from connected battery and inverter systems.' },
  { pattern: /^\/terms$/, title: 'Terms of Service', description: 'The terms covering Powston controller installs and pure-subscription access to the Powston trading platform.' },
  { pattern: /^\/system-status$/, title: 'System status', description: 'Check the current status of Powston services.' },
  { pattern: /^\/performance(?:\/.*)?$/, title: 'Performance', description: 'Review energy-asset performance and verification data.' },
  { pattern: /^\/rules(?:\/.*)?$/, title: 'Trading rules', description: 'Review and test inverter trading rules.' },
  { pattern: /^\/blockly(?:\/.*)?$/, title: 'Rule editor', description: 'Build and test inverter trading logic.' },
  { pattern: /^\/dashboard(?:\/.*)?$/, title: 'Dashboard', description: 'Review Powston operational information.' },
  { pattern: /^\/onboard(?:\/.*)?$/, title: 'Site setup', description: 'Complete the steps required to prepare a site for management.' },
  { pattern: /^\/pdrs-registration$/, title: 'PDRS registration', description: 'Register an eligible energy system.' },
];

function fallbackTitle(pathname: string): string {
  const looksLikeTechnicalIdentifier = (segment: string) =>
    /^\d+$/.test(segment)
    || /^(?=.*\d)[a-z0-9:._-]{8,}$/i.test(segment)
    || /^[a-f0-9:.-]{8,}$/i.test(segment);
  const meaningfulSegment = pathname
    .split('/')
    .filter(Boolean)
    .reverse()
    .find((segment) => !looksLikeTechnicalIdentifier(segment));

  if (!meaningfulSegment) return 'Powston';
  return meaningfulSegment
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function getRouteMetadata(pathname: string | null | undefined): RouteMetadata {
  const path = pathname || '/';
  for (const route of routes) {
    const match = path.match(route.pattern);
    if (match) {
      return {
        title: typeof route.title === 'function' ? route.title(match) : route.title,
        description: route.description,
      };
    }
  }
  return { title: fallbackTitle(path), description: DEFAULT_DESCRIPTION };
}

export function formatDocumentTitle(title: string): string {
  return title === 'Powston' ? title : `${title} | Powston`;
}

/**
 * Shared route metadata should fill in generic/default titles, not replace a
 * title deliberately supplied by a page with more specific context.
 */
export function canApplySharedRouteTitle(
  currentTitle: string,
  routeTitle: string,
  previousSharedTitle: string | null = null,
  previousPageTitle: string | null = null,
): boolean {
  const current = normalizeMetadataText(currentTitle);
  return current === ''
    || current === normalizeMetadataText('Powston')
    || current === normalizeMetadataText(previousSharedTitle)
    || current === normalizeMetadataText(previousPageTitle)
    || current === normalizeMetadataText(routeTitle)
    || current === normalizeMetadataText(formatDocumentTitle(routeTitle));
}

export function canApplySharedRouteDescription(
  currentDescription: string,
  routeDescription: string,
  previousSharedDescription: string | null = null,
  previousPageDescription: string | null = null,
): boolean {
  const current = normalizeMetadataText(currentDescription);
  return current === ''
    || current === normalizeMetadataText(DEFAULT_DESCRIPTION)
    || current === normalizeMetadataText(previousSharedDescription)
    || current === normalizeMetadataText(previousPageDescription)
    || current === normalizeMetadataText(routeDescription);
}
